import React, { useState } from 'react';

import AddCircleOutlineRoundedIcon from '@mui/icons-material/AddCircleOutlineRounded';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Box, Button, IconButton, Stack, Typography } from '@mui/material';
import { GridRowId } from '@mui/x-data-grid';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { enqueueSnackbar } from 'notistack';

import CreateGroupsModal from './CreateGroupsModal';
import GroupsTable from './GroupsTable';
import { useUserData } from '../App/Component/UserDataProvider';
import { IObject } from '../interfaces/IObject';
import ReusableConfirmationModal from '../RiverusUI/Components/ReusableConfirmationModal';
import { deleteGroup, fetchKeycloakGroups } from '../Services/keycloak';
import { QueryKeyGenerator } from '../Utils/QueryKeyGenerator';

const GroupsComponent = () => {
  const [selectedRow, setSelectedRow] = useState<GridRowId[]>([]);
  const [pageNumber, setPageNumberChange] = useState<number>(0);
  const [filters, setFilters] = useState<IObject>({
    groups: null,
    actions: null,
  });
  const [openCreateGroup, setOpenCreateGroup] = useState<boolean>(false);
  const [groupsTableKey, setGroupsTableKey] = useState<number>(0);

  const [editDetails, setEditDetails] = useState<any>(null);
  const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);
  const [deleteDocId, setDeleteDocId] = useState<any>('');

  const { userIsAdmin } = useUserData();

  const handleClose = () => {
    setEditDetails(false);
    setOpenCreateGroup(false);
  };

  const queryClient = useQueryClient();

  const handleCloseDeleteConfirmation = () => {
    setDeleteDocId('');
    setOpenDeleteModal(false);
  };

  const { mutate: deleteGroupMutation } = useMutation({
    mutationFn: deleteGroup,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [
          QueryKeyGenerator.getRequestApproval(),
          'keycloak_groups',
          groupsTableKey,
        ],
      });
      handleCloseDeleteConfirmation();
      enqueueSnackbar('Deleted group successfully!', {
        variant: 'success',
        anchorOrigin: { vertical: 'top', horizontal: 'right' },
      });
    },
  });

  const {
    data: groupData,
    isLoading,
    isFetching,
  } = useQuery({
    queryKey: [
      QueryKeyGenerator.getRequestApproval(),
      'keycloak_groups',
      groupsTableKey,
    ],
    queryFn: fetchKeycloakGroups,
  });

  const handleDelete = React.useCallback(
    (ids: GridRowId[]) => {
      if (ids.length > 0) {
        setOpenDeleteModal(true);
        setDeleteDocId(ids);
      }
    },
    [deleteGroupMutation]
  );

  const handleEdit = (data: any) => {
    setEditDetails(data);
    setOpenCreateGroup(true);
  };

  const renderTable = React.useCallback(
    () => (
      <GroupsTable
        key={groupsTableKey}
        selectedRow={selectedRow}
        setSelectedRow={setSelectedRow}
        data={groupData}
        isLoading={isLoading || isFetching}
        pageNumber={pageNumber}
        setPageNumberChange={setPageNumberChange}
        setFilters={setFilters}
        deleteGroup={handleDelete}
        editGroup={handleEdit}
        filters={filters}
        editDetails={editDetails}
      />
    ),
    [
      groupData,
      handleDelete,
      isFetching,
      isLoading,
      pageNumber,
      selectedRow,
      groupsTableKey,
    ]
  );

  return (
    <Stack>
      <Stack className="sidebar-right-column" gap="16px">
        <Stack direction="column" spacing="10px">
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
            Create
          </Typography>
          <Button
            sx={{
              color: 'white',
              backgroundColor: 'riPrimary.500',
              padding: '4px 14px',
              borderRadius: '6px',
              fontSize: '13px',
              width: 'fit-content',
              display: 'flex',
              flexDirection: 'column',
              lineHeight: 1,
              alignItems: 'center',
              height: '4rem',
              textTransform: 'capitalize',
              minWidth: '8rem',
              margin: '0',
              '&:hover': {
                backgroundColor: 'rgba(109, 38, 76, 0.1)',
                color: '#6D264C',
              },
              '&:disabled': {
                border: '2px solid rgba(0, 0, 0, 0.26)',
                background: 'unset',
              },
            }}
            onClick={() => {
              setOpenCreateGroup(true);
            }}
            disabled={!userIsAdmin}
          >
            <AddCircleOutlineRoundedIcon
              fontSize="small"
              sx={{ marginBottom: '6px' }}
            />
            New Group
          </Button>
          <Box sx={{ width: '100%' }}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: '10px' }}>
              <IconButton onClick={() => setGroupsTableKey(Math.random())}>
                <RefreshIcon />
              </IconButton>
            </Box>
            {renderTable()}
          </Box>
        </Stack>
      </Stack>
      {openCreateGroup && (
        <CreateGroupsModal
          open={openCreateGroup}
          onClose={handleClose}
          groupsTableKey={groupsTableKey}
          editDetails={editDetails}
        />
      )}
      {openDeleteModal && (
        <ReusableConfirmationModal
          open={openDeleteModal}
          onClose={handleCloseDeleteConfirmation}
          title="Delete Group"
          cancelBtnText="No, Go Back"
          confirmBtnText="Yes, Delete"
          onConfirm={() => deleteGroupMutation(deleteDocId)}
        >
          <Stack spacing={2}>
            <Typography>Are you sure?</Typography>
            <Typography>
              The selected group will be removed permanently.
            </Typography>
            <Typography>Note: This action is not reversible.</Typography>
          </Stack>
        </ReusableConfirmationModal>
      )}
    </Stack>
  );
};

export default GroupsComponent;
